/**
 * Pure cross-section text search for the Find & Replace widget.
 *
 * Returns hits as STABLE COORDINATES — no live DOM references. Each hit
 * carries (sectionPath, textNodeOrdinal, matchStart, matchEnd, beforeCtx,
 * match, afterCtx, queryFingerprint), so the replace pass can revalidate
 * against a freshly-parsed section.html and skip stale hits gracefully.
 *
 * Iteration order matches iterateAllSections: shared nav → all pages
 * (in page/section order) → shared footer. That's the natural reading
 * order users expect to see results in.
 */

import { walkTextNodes } from './text-walker.js';
import { iterateAllSections } from './iterate-sections.js';
import { getClientTextBindingByKey, resolveClientText } from './client-info.js';

// Module-level Map<htmlString, ParsedSection> cache, size-capped LRU.
// MUST be Map (not WeakMap) because keys are strings.
const _parsedCache = new Map();
const PARSE_CACHE_LIMIT = 500;

function getParsedSection(html) {
  let entry = _parsedCache.get(html);
  if (entry) {
    // Touch for LRU — re-insert
    _parsedCache.delete(html);
    _parsedCache.set(html, entry);
    return entry;
  }
  const doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
  entry = { doc, root: doc.body.firstElementChild };
  _parsedCache.set(html, entry);
  if (_parsedCache.size > PARSE_CACHE_LIMIT) {
    _parsedCache.delete(_parsedCache.keys().next().value);
  }
  return entry;
}

/**
 * Search every section in state for `query`.
 *
 * Always returns { results, error, fingerprint } — never a bare array,
 * never undefined. UI code branches once on `error` and consumes `results`
 * uniformly otherwise.
 *
 * opts:
 *   - caseSensitive (default false)
 *   - wholeWord     (default false)
 *   - regex         (default false)
 *   - includeFormText (default false) — descend into <label>/<button> text
 *
 * Errors:
 *   - Invalid user-typed regex → { results:[], error:'<regex msg>', fingerprint }
 *   - Empty query, empty state → { results:[], error:null, fingerprint }
 */
export function search(state, query, opts = {}) {
  const fingerprint = computeFingerprint(query, opts);
  if (!state || !query) {
    return { results: [], error: null, fingerprint };
  }
  const { caseSensitive = false, wholeWord = false, regex = false, includeFormText = false } = opts;
  if (regex && looksCatastrophicRegex(query)) {
    return { results: [], error: 'Regex too complex — try anchoring it.', fingerprint };
  }
  let matcher;
  try {
    matcher = buildMatcher(query, { caseSensitive, wholeWord, regex });
  } catch (err) {
    return { results: [], error: err.message || String(err), fingerprint };
  }

  const results = [];
  let slowRegexError = null;
  iterateAllSections(state, (ctx) => {
    if (slowRegexError) return;
    const { root } = getParsedSection(ctx.html);
    if (!root) return;
    const hits = [];
    let textNodeOrdinal = -1;
    const deadlineMs = regex ? nowMs() + 50 : Infinity;
    let timedOut = false;
    walkTextNodes(
      root,
      (textNode) => {
        if (timedOut) return;
        textNodeOrdinal++;
        timedOut = collectHitsInNode(textNode, matcher, textNodeOrdinal, hits, {
          clientInfo: state.clientInfo,
          projectName: state.projectName,
          deadlineMs
        });
      },
      { includeFormText, includeBoundText: true }
    );
    if (timedOut) {
      slowRegexError = 'Regex too slow — try anchoring it.';
      return;
    }
    if (hits.length) {
      results.push({
        sectionPath: ctx.sectionPath,
        instanceId: ctx.instanceId,
        kind: ctx.kind,
        pageId: ctx.pageId || null,
        pageName: ctx.pageName || (ctx.sharedKind ? 'All pages' : ''),
        sharedKind: ctx.sharedKind || null,
        queryFingerprint: fingerprint,
        hits
      });
    }
  });
  return { results: slowRegexError ? [] : results, error: slowRegexError, fingerprint };
}

/**
 * Compute a stable hash-like fingerprint of (query, opts) — used by replace
 * to detect stale results pinned to a previous query.
 *
 * JSON-stringified normalized tuple is plenty for our purposes; this is not
 * a cryptographic primitive.
 */
export function computeFingerprint(query, opts = {}) {
  return JSON.stringify([
    String(query || ''),
    !!opts.caseSensitive,
    !!opts.wholeWord,
    !!opts.regex,
    !!opts.includeFormText
  ]);
}

function buildMatcher(query, { caseSensitive, wholeWord, regex }) {
  const flags = 'g' + (caseSensitive ? '' : 'i');
  if (regex) return new RegExp(query, flags);
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(wholeWord ? '\\b' + escaped + '\\b' : escaped, flags);
}

/**
 * Per text node, push hits into `hits`. Defends against zero-length regex
 * matches (^, \b, (?=foo)) — those would infinite-loop a global /g regex
 * unless lastIndex is advanced manually. Zero-length matches are also
 * rejected from the hit list (they have no slice to wrap, and listing
 * them would confuse the UI).
 *
 * Hard safety cap: max iterations = 10 × text length. Practically nothing
 * legitimate hits this, but a runaway regex shouldn't lock the UI.
 */
function collectHitsInNode(textNode, matcher, textNodeOrdinal, hits, context = {}) {
  const binding = nearestBinding(textNode);
  const text = binding
    ? resolveClientText(
        binding.key,
        context.clientInfo,
        'preview',
        context.projectName || 'Untitled'
      )
    : textNode.nodeValue || '';
  matcher.lastIndex = 0;
  const safetyCap = 10 * (text.length + 1);
  let iter = 0;
  let m;
  while ((m = matcher.exec(text)) !== null) {
    if (nowMs() > context.deadlineMs) return true;
    if (++iter > safetyCap) break;
    const matchLen = m[0].length;
    if (matchLen > 0) {
      hits.push(buildHit(textNode, m, text, textNodeOrdinal, binding));
    }
    if (m.index === matcher.lastIndex) {
      matcher.lastIndex++;
    }
    if (!matcher.global) break;
  }
  return false;
}

function buildHit(textNode, match, text, textNodeOrdinal, binding = null) {
  const matchStart = match.index;
  const matchEnd = match.index + match[0].length;
  return {
    hitId: cryptoRandomId(),
    eid: nearestEid(textNode),
    textNodeOrdinal,
    matchStart,
    matchEnd,
    match: match[0],
    beforeCtx: text.slice(Math.max(0, matchStart - 32), matchStart),
    afterCtx: text.slice(matchEnd, matchEnd + 32),
    captureGroups: match.slice(1),
    isInsideBinding: !!binding,
    bindingKey: binding ? binding.key : null,
    bindingLabel: binding ? binding.label : null,
    isInsideForm: hasFormAncestor(textNode)
  };
}

function cryptoRandomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Test/Node-without-webcrypto fallback
  return 'fr-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function nearestEid(textNode) {
  let cur = textNode.parentNode;
  while (cur && cur.nodeType === 1) {
    if (cur.getAttribute) {
      const eid = cur.getAttribute('data-pb-eid');
      if (eid) return eid;
    }
    cur = cur.parentNode;
  }
  return null;
}

function nearestBinding(textNode) {
  let cur = textNode.parentNode;
  while (cur && cur.nodeType === 1) {
    if (cur.hasAttribute) {
      const key =
        cur.getAttribute('data-pb-client') || cur.getAttribute('data-pb-client-text') || '';
      const def = key ? getClientTextBindingByKey(key) : null;
      if (def) return { key, label: def.label || key };
    }
    cur = cur.parentNode;
  }
  return null;
}

const FORM_ANCESTOR_TAGS = new Set([
  'FORM',
  'INPUT',
  'TEXTAREA',
  'SELECT',
  'OPTION',
  'LABEL',
  'BUTTON'
]);
function hasFormAncestor(textNode) {
  let cur = textNode.parentNode;
  while (cur && cur.nodeType === 1) {
    if (FORM_ANCESTOR_TAGS.has(cur.tagName)) return true;
    cur = cur.parentNode;
  }
  return false;
}

function nowMs() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function looksCatastrophicRegex(query) {
  const source = String(query || '');
  // Common nested-quantifier backtracking footguns: (a+)+, (.*){2}, ([\s\S]*)*
  // These can stall inside a single RegExp.exec call, before a wall-clock check
  // gets control back.
  return /\((?:[^()\\]|\\.)*[*+{](?:[^()\\]|\\.)*\)\s*[*+{]/.test(source);
}
